import { IonCheckbox, IonIcon, IonItem, IonLabel } from "@ionic/react";
import React from "react";
import {
  chevronDownOutline as downIcon,
  chevronUpOutline as upIcon,
  checkmark,
} from "ionicons/icons";
import clsx from "clsx";

import { IGearboxTeacher, ITeacher } from "../../state";
import triangleIcon from "../../assets/icons/triangle_down.svg";
import ToolTip from "../../atoms/ToolTip";

import styles from "./TeacherSelectForm.module.scss";

export interface TeacherSelectFormProps {
  isOpen: boolean;
  label?: string;
  description?: string;
  teachers: ITeacher[];
  submitted_teachers?: IGearboxTeacher[];
  selected_teacher_ids: number[];
  disabled_teacher_ids?: number[];
  disabledReason?: string;
  checkboxSelect?: boolean;
  whiteMode?: boolean;
  className?: string;
  onClick: () => void;
  update: (args: { [key: string]: any }) => void;
  disabled?: boolean;
  isSecond?: boolean;
}

const TeacherSelectForm = (props: TeacherSelectFormProps) => {
  const teacherList = React.useMemo(
    () =>
      [...props.teachers, ...(props.submitted_teachers ?? [])].reduce(
        (uniqueTeacherList: IGearboxTeacher[], teacher) =>
          !uniqueTeacherList.some(
            (uniqueTeacher: IGearboxTeacher) => uniqueTeacher.id === teacher.id,
          )
            ? [
                ...uniqueTeacherList,
                { id: teacher.id, full_name: teacher.full_name ?? "" },
              ]
            : uniqueTeacherList,
        [],
      ),
    [props.submitted_teachers, props.teachers],
  );

  const displayNames =
    props.selected_teacher_ids.length === 0
      ? "未選択"
      : props.selected_teacher_ids.length >= 2
      ? `${
          teacherList.filter(t => {
            return t.id === props.selected_teacher_ids[0];
          })[0]?.full_name
        } ...他${props.selected_teacher_ids.length - 1}人`
      : teacherList.filter(t => {
          return t.id === props.selected_teacher_ids[0];
        })[0]?.full_name;

  const toggleTeacher = (teacherId: number) => {
    if (props.selected_teacher_ids.includes(teacherId)) {
      props.update({
        selected_teacher_ids: props.selected_teacher_ids.filter(
          id => id !== teacherId,
        ),
      });
    } else {
      props.update({
        selected_teacher_ids: [...props.selected_teacher_ids, teacherId],
      });
    }
  };

  return (
    <>
      <IonItem
        className={clsx(
          styles.teacherItemToggle,
          props.disabled && styles.disabled,
          props.isSecond && styles.second,
          props.whiteMode && styles.whiteMode,
          props.className || "",
        )}
        onClick={props.onClick}
      >
        <IonLabel color="light" className={styles.title}>
          {props.label ?? "提出先"}
        </IonLabel>
        <div className={styles.selectedNames}>{displayNames}</div>

        {props.whiteMode ? (
          <IonIcon
            icon={triangleIcon}
            slot="end"
            className={clsx(
              styles.triangleIcon,
              props.isOpen && styles.triangleIconOpen,
            )}
          />
        ) : props.isOpen ? (
          <IonIcon icon={upIcon} slot="end" className={styles.arrowIcon} />
        ) : (
          <IonIcon icon={downIcon} slot="end" className={styles.arrowIcon} />
        )}
      </IonItem>
      <div
        className={clsx(
          props.isOpen
            ? props.checkboxSelect
              ? styles.checkboxArea
              : styles.teacherArea
            : styles.close,
          props.whiteMode && styles.whiteModeArea,
        )}
      >
        {props.checkboxSelect ? (
          <div className={styles.gradePanel}>
            {props.description && (
              <p className={styles.description}>{props.description}</p>
            )}
            <ul className={styles.teacherCheckboxList}>
              {teacherList.map(teacher => {
                const isTeacherDisabled =
                  props.disabled_teacher_ids?.includes(teacher.id) ?? false;
                const content = (
                  <>
                    <IonCheckbox
                      checked={props.selected_teacher_ids.includes(teacher.id)}
                      className={styles.gradeCheckbox}
                    />
                    <span className={styles.gradeTeacherName}>
                      {teacher.full_name}
                    </span>
                  </>
                );
                return (
                  <li
                    key={teacher.id}
                    className={clsx(
                      styles.teacherCheckboxItem,
                      isTeacherDisabled && styles.disabledItem,
                    )}
                    onClick={() => {
                      if (isTeacherDisabled) {
                        return;
                      }
                      toggleTeacher(teacher.id);
                    }}
                  >
                    {isTeacherDisabled && props.disabledReason ? (
                      <ToolTip
                        content={props.disabledReason}
                        placement="bottomLeft"
                        multiline
                      >
                        {content}
                      </ToolTip>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <ul className={styles.teacherSelector}>
            {teacherList.map(teacher => (
              <li
                key={teacher.id}
                className={styles.teacherItem}
                onClick={() => toggleTeacher(teacher.id)}
              >
                <div className={styles.teacherName}>
                  {teacher.full_name}
                  {props.selected_teacher_ids.includes(teacher.id) && (
                    <IonIcon
                      slot="end"
                      icon={checkmark}
                      className={styles.checkIcon}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default TeacherSelectForm;
